import { useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Calendar, Mail, Clock, User } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

interface VincerBaseUser {
  name: string;
  email: string;
}

interface MentorData {
  uuid: string;
  vincer_base_user: VincerBaseUser;
}

interface MenteeData {
  uuid: string;
  vincer_base_user: VincerBaseUser;
}

interface Assignment {
  uuid: string;
  start: string;
  end: string | null;
  mentor: {
    name: string;
    email: string;
  };
  mentee: {
    name: string;
    email: string;
  };
}

export function AssignmentDetails() {
  const { id } = useParams();
  const [assignment, setAssignment] = useState<Assignment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchAssignment() {
      if (!id) return;

      try {
        const { data, error: fetchError } = await supabase
          .from('relationship_assignments')
          .select(`
            uuid,
            start,
            end,
            mentor:mentor_uuid (
              uuid,
              vincer_base_user (
                name,
                email
              )
            ),
            mentee:mentee_uuid (
              uuid,
              vincer_base_user (
                name,
                email
              )
            )
          `)
          .eq('uuid', id)
          .single();

        if (fetchError) throw fetchError;

        if (data) {
          const typedData = data as unknown as {
            uuid: string;
            start: string;
            end: string | null;
            mentor: {
              uuid: string;
              vincer_base_user: {
                name: string;
                email: string;
              };
            };
            mentee: {
              uuid: string;
              vincer_base_user: {
                name: string;
                email: string;
              };
            };
          };

          setAssignment({
            uuid: typedData.uuid,
            start: typedData.start,
            end: typedData.end,
            mentor: {
              name: typedData.mentor.vincer_base_user.name,
              email: typedData.mentor.vincer_base_user.email,
            },
            mentee: {
              name: typedData.mentee.vincer_base_user.name,
              email: typedData.mentee.vincer_base_user.email,
            },
          });
        }
      } catch (err) {
        console.error('Error fetching assignment:', err);
        setError('Failed to load assignment details');
      } finally {
        setLoading(false);
      }
    }

    fetchAssignment();
  }, [id]);

  const getStatus = () => {
    if (!assignment) return 'unknown';
    return assignment.end ? 'completed' : 'active';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#ffcc32] mx-auto mb-4"></div>
        <p className="text-gray-600">Loading assignment details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto text-center py-12">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  if (!assignment) {
    return (
      <div className="max-w-4xl mx-auto text-center py-12">
        <p className="text-gray-600">Assignment not found</p>
      </div>
    );
  }

  const status = getStatus();

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold">Assignment Details</h1>
        <span className={`px-4 py-2 rounded-full text-sm font-medium ${getStatusColor(status)}`}>
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
      </div>

      <div className="grid gap-6">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white p-6 rounded-xl shadow-sm border">
            <div className="flex items-center gap-3 mb-4">
              <User className="w-5 h-5 text-[#ffcc32]" />
              <h2 className="text-xl font-semibold">Mentor</h2>
            </div>
            <div className="space-y-2">
              <p className="text-lg font-medium">{assignment.mentor.name}</p>
              <p className="text-gray-600">{assignment.mentor.email}</p>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border">
            <div className="flex items-center gap-3 mb-4">
              <User className="w-5 h-5 text-[#ffcc32]" />
              <h2 className="text-xl font-semibold">Mentee</h2>
            </div>
            <div className="space-y-2">
              <p className="text-lg font-medium">{assignment.mentee.name}</p>
              <p className="text-gray-600">{assignment.mentee.email}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border">
          <div className="flex items-center gap-3 mb-4">
            <Calendar className="w-5 h-5 text-[#ffcc32]" />
            <h2 className="text-xl font-semibold">Assignment Details</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <p className="text-sm font-medium text-gray-500 mb-1">Started</p>
              <p className="text-gray-900">
                {new Date(assignment.start).toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </p>
            </div>
            {assignment.end && (
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">Completed</p>
                <p className="text-gray-900">
                  {new Date(assignment.end).toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-4">
          <button className="flex items-center gap-2 bg-[#ffcc32] text-black px-6 py-3 rounded-lg hover:bg-[#ffd65c] transition-colors">
            <Mail className="w-5 h-5" />
            <span>Send Message</span>
          </button>
          {!assignment.end && (
            <button className="flex items-center gap-2 bg-[#4CAF50] text-white px-6 py-3 rounded-lg hover:bg-[#45a049] transition-colors">
              <Clock className="w-5 h-5" />
              <span>Mark as Completed</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}